import { useState } from 'react';
import { 
  BrowserRouter as Router, 
  Routes, 
  Route, 
  NavLink, 
  Outlet,
  useLocation 
} from 'react-router-dom';
import { 
  FiHome, 
  FiUsers, 
  FiCalendar, 
  FiFileText, 
  FiSettings, 
  FiBarChart2,
  FiMenu,
  FiX,
  FiSearch
} from 'react-icons/fi';

function App() {
  return (
    <Router>
      <div className="min-h-screen bg-gray-50">
        <Routes>
          <Route path="/" element={<Layout />}>
            <Route index element={<Dashboard />} />
            <Route path="customers" element={<Customers />} />
            <Route path="appointments" element={<Appointments />} />
            <Route path="reports" element={<Reports />} />
            <Route path="documents" element={<Documents />} />
            <Route path="settings" element={<Settings />} />
          </Route>
        </Routes>
      </div>
    </Router>
  );
}

// Layout Component
function Layout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  
  const navItems = [
    { name: 'Dashboard', path: '/', icon: <FiHome size={20} /> },
    { name: 'Customers', path: '/customers', icon: <FiUsers size={20} /> },
    { name: 'Appointments', path: '/appointments', icon: <FiCalendar size={20} /> },
    { name: 'Reports', path: '/reports', icon: <FiBarChart2 size={20} /> },
    { name: 'Documents', path: '/documents', icon: <FiFileText size={20} /> },
    { name: 'Settings', path: '/settings', icon: <FiSettings size={20} /> },
  ];

  return (
    <div className="flex h-screen">
      {/* Mobile Sidebar Toggle */}
      <button 
        onClick={() => setSidebarOpen(!sidebarOpen)}
        className="md:hidden fixed top-4 left-4 z-50 p-2 rounded-lg bg-primary text-white"
      >
        {sidebarOpen ? <FiX size={24} /> : <FiMenu size={24} />}
      </button>

      {/* Sidebar */}
      <div 
        className={`fixed md:static z-40 w-64 bg-dark text-white transition-all duration-300 ease-in-out
          ${sidebarOpen ? 'left-0' : '-left-64'} md:left-0 h-full`}
      >
        <div className="p-6 border-b border-gray-700">
          <h1 className="text-2xl font-bold">CRM Pro</h1>
          <p className="text-gray-400 text-sm">Customer Management System</p>
        </div>
        
        <nav className="mt-6">
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              className={({isActive}) => 
                `flex items-center px-6 py-3 transition-colors duration-200
                ${isActive ? 'bg-primary' : 'hover:bg-gray-800'}`
              }
              onClick={() => setSidebarOpen(false)}
            >
              <span className="mr-3">{item.icon}</span>
              <span>{item.name}</span>
            </NavLink>
          ))}
        </nav>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        <header className="bg-white shadow-sm">
          <div className="px-6 py-4 flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-800 capitalize">
              {location.pathname.split('/')[1] || 'Dashboard'}
            </h2>
            <div className="flex items-center space-x-4">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FiSearch className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  className="block w-full pl-10 pr-3 py-2 rounded-md border border-gray-300 focus:outline-none focus:ring-primary focus:border-primary sm:text-sm"
                  placeholder="Search..."
                  type="search"
                />
              </div>
              <div className="h-10 w-10 rounded-full bg-primary flex items-center justify-center text-white font-semibold">
                JD
              </div>
            </div>
          </div>
        </header>
        
        <main className="flex-1 overflow-y-auto p-6 bg-gray-50">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

// Placeholder Pages
function Dashboard() {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {[1, 2, 3, 4, 5, 6].map((item) => (
        <div key={item} className="bg-white p-6 rounded-xl shadow">
          <h3 className="text-lg font-medium mb-4">Card {item}</h3>
          <p className="text-gray-600">
            This is a sample dashboard card with placeholder content.
          </p>
        </div>
      ))}
    </div>
  );
}

function Customers() {
  return (
    <div className="bg-white p-6 rounded-xl shadow">
      <h2 className="text-xl font-semibold mb-4">Customers</h2>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead>
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {[...Array(8)].map((_, i) => (
              <tr key={i}>
                <td className="px-6 py-4 whitespace-nowrap">Customer {i+1}</td>
                <td className="px-6 py-4 whitespace-nowrap">customer{i+1}@example.com</td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                    Active
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Appointments() {
  return (
    <div className="bg-white p-6 rounded-xl shadow">
      <h2 className="text-xl font-semibold mb-4">Appointments</h2>
      <div className="space-y-4">
        {[...Array(5)].map((_, i) => (
          <div key={i} className="flex items-start p-4 border border-gray-200 rounded-lg">
            <div className="flex-1">
              <h3 className="font-medium">Meeting with Client #{i+1}</h3>
              <p className="text-gray-600 text-sm">Tomorrow at 10:00 AM</p>
            </div>
            <button className="ml-4 px-3 py-1 bg-primary text-white rounded-md text-sm">
              View
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function Reports() {
  return (
    <div className="bg-white p-6 rounded-xl shadow">
      <h2 className="text-xl font-semibold mb-4">Reports</h2>
      <div className="h-64 bg-gray-100 rounded-lg flex items-center justify-center">
        <p className="text-gray-500">Sales chart visualization</p>
      </div>
    </div>
  );
}

function Documents() {
  return (
    <div className="bg-white p-6 rounded-xl shadow">
      <h2 className="text-xl font-semibold mb-4">Documents</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {[...Array(6)].map((_, i) => (
          <div key={i} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
            <div className="h-12 w-12 bg-gray-200 rounded-lg mb-3"></div>
            <h3 className="font-medium">Document_{i+1}.pdf</h3>
            <p className="text-gray-500 text-sm">Updated 2 days ago</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function Settings() {
  return (
    <div className="bg-white p-6 rounded-xl shadow">
      <h2 className="text-xl font-semibold mb-4">Settings</h2>
      <div className="space-y-6">
        <div>
          <h3 className="font-medium mb-2">Account Settings</h3>
          <div className="space-y-3 pl-4">
            <div className="flex items-center">
              <input type="checkbox" className="mr-2" defaultChecked />
              <span>Email notifications</span>
            </div>
            <div className="flex items-center">
              <input type="checkbox" className="mr-2" />
              <span>SMS notifications</span>
            </div>
          </div>
        </div>
        
        <div>
          <h3 className="font-medium mb-2">System Preferences</h3>
          <select className="border rounded p-2 w-full max-w-xs">
            <option>Light Theme</option>
            <option>Dark Theme</option>
            <option>System Default</option>
          </select>
        </div>
      </div>
    </div>
  );
}

export default App;